import { APP_PATHS } from '../constants/appPaths';

/** URL de edição de usuário (perfil próprio ou admin editando outro). */
export const getUserEditPath = (userId) => `${APP_PATHS.USERS}/${userId}/edit`;

export const getUsersListPath = () => APP_PATHS.USERS;
export const getUserNewPath = () => `${APP_PATHS.USERS}/new`;
export const getUserSchedulesPath = () => `${APP_PATHS.USERS}/user-schedules`;
export const getUserAppointmentsPath = (search = '') =>
  `${APP_PATHS.USERS}/appointments${search ? (search.startsWith('?') ? search : `?${search}`) : ''}`;

/** Proprietário da empresa (campo vindo de /auth/me ou /users/:id). */
export const isUserOwner = (user) =>
  Boolean(user?.isOwner === true || user?.isOwner === 'true' || user?.owner === true);

/** Super admin da plataforma (campo vindo de /auth/me ou /users/:id). */
export const isUserSuperAdmin = (user) =>
  Boolean(user?.super === true || user?.super === 'true' || user?.super === 1);

/** Usa super do usuário ou do auth quando é o próprio perfil. */
export const resolveUserSuperAdminStatus = (user, authUser) => {
  if (!user) return false;
  if (isUserSuperAdmin(user)) return true;
  if (authUser?.id != null && user.id === authUser.id && isUserSuperAdmin(authUser)) return true;
  return false;
};

/** ID do usuário proprietário no payload de company (/auth/me ou /companies/:id). */
export const extractCompanyOwnerUserId = (company) => {
  if (!company || typeof company !== 'object') return null;
  const id =
    company.ownerId ??
    company.ownerUserId ??
    company.userId ??
    company.owner?.id ??
    company.owner?.userId ??
    null;
  return id != null && id !== '' ? id : null;
};

export const enrichUserWithOwner = (user, ownerUserId, meUser) => {
  if (!user) return user;
  if (isUserOwner(user)) return user;
  if (meUser?.id != null && user.id === meUser.id && isUserOwner(meUser)) {
    return { ...user, isOwner: true };
  }
  if (ownerUserId != null && String(user.id) === String(ownerUserId)) {
    return { ...user, isOwner: true };
  }
  return user;
};

/** GET /users/:id traz isOwner e super; a listagem nem sempre — complementa flags ausentes. */
export const enrichUsersOwnerFromDetails = async (users, fetchUser) => {
  if (!Array.isArray(users) || !users.length || typeof fetchUser !== 'function') {
    return users;
  }

  const pending = users.filter(
    (user) => !isUserOwner(user) || user.super === undefined || user.super === null
  );
  if (!pending.length) return users;

  const detailById = new Map();
  await Promise.all(
    pending.map(async (user) => {
      try {
        const data = await fetchUser(user.id);
        if (data?.id != null) detailById.set(data.id, data);
      } catch (_) {
        /* ignore */
      }
    })
  );

  if (!detailById.size) return users;

  return users.map((user) => {
    const detail = detailById.get(user.id);
    if (!detail) return user;
    let next = user;
    if (isUserOwner(detail)) next = { ...next, isOwner: true };
    if (isUserSuperAdmin(detail)) next = { ...next, super: true };
    else if (detail.super === false) next = { ...next, super: false };
    return next;
  });
};

/** Usa isOwner do usuário, do auth (mesmo id) ou do proprietário da empresa. */
export const resolveUserOwnerStatus = (user, authUser, ownerUserId) => {
  if (!user) return false;
  if (isUserOwner(user)) return true;
  if (authUser?.id != null && user.id === authUser.id && isUserOwner(authUser)) return true;
  if (ownerUserId != null && String(user.id) === String(ownerUserId)) return true;
  return false;
};

/** Plano, faturas e assinatura — proprietário da empresa (super admin incluído).
 * Owner-only por design: o profile (admin/user) NÃO decide — só super/isOwner.
 * Fonte única; subscriptionAccess.js re-exporta daqui. */
export const canManageSubscription = (user) => {
  if (!user) return false;
  if (isUserSuperAdmin(user)) return true;
  if (isUserOwner(user)) return true;
  const ownerId = extractCompanyOwnerUserId(user?.company);
  return ownerId != null && String(ownerId) === String(user.id);
};
